import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Edit, Trash2, BookOpen, Volume2, Loader, Mic } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { vocabularyAPI, categoriesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import AudioRecorder from './AudioRecorder';

const VocabularyManager = () => {
  const { user } = useAuth();
  const [words, setWords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingWord, setEditingWord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [recordingWord, setRecordingWord] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [updatingWordId, setUpdatingWordId] = useState(null);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const { toast } = useToast();
  
  // Scroll position tracking with ref
  const scrollPositionRef = useRef(0);
  const shouldRestoreScroll = useRef(false);
  const previousDialogState = useRef(false);

  // Save scroll position before operations
  const saveScrollPosition = () => {
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
    shouldRestoreScroll.current = true;
  };

  // Restore scroll position after dialog closes
  const restoreScrollPosition = () => {
    if (shouldRestoreScroll.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        window.scrollTo({
          top: scrollPositionRef.current,
          behavior: 'instant'
        });
        shouldRestoreScroll.current = false;
      }, 100);
    }
  };

  // Watch for dialog close and restore scroll
  useEffect(() => {
    // Dialog just closed (was true, now false)
    if (previousDialogState.current === true && isAddDialogOpen === false) {
      restoreScrollPosition();
    }
    previousDialogState.current = isAddDialogOpen;
  }, [isAddDialogOpen]);

  const [newWord, setNewWord] = useState({
    algerianWord: '',
    englishTranslation: '',
    pronunciation: '',
    arabicScript: '',
    exampleSentence: '',
    category: '',
    difficulty: 'beginner',
    wordType: '',
    possessiveType: ''
  });

  // Load initial data
  useEffect(() => {
    loadVocabulary();
    loadCategories();
  }, []);

  // Load vocabulary with current filters
  useEffect(() => {
    const filters = {};
    if (selectedCategory !== 'all') filters.category = selectedCategory;
    if (searchTerm.trim()) filters.search = searchTerm.trim();
    
    loadVocabulary(filters);
  }, [selectedCategory, searchTerm]);

  const loadVocabulary = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await vocabularyAPI.getAll(filters);
      setWords(response.words || []);
    } catch (error) {
      toast({
        title: "Error Loading Vocabulary",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await categoriesAPI.getAll();
      setCategories(categoriesData || []);
    } catch (error) {
      toast({
        title: "Error Loading Categories",
        description: error.message,
        variant: "destructive"
      });
    }
  };


  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        title: "Category Name Required",
        description: "Please enter a category name",
        variant: "destructive"
      });
      return;
    }

    try {
      setCreatingCategory(true);
      const newCategory = await categoriesAPI.create({
        name: newCategoryName.trim(),
        icon: '📚', // Default icon
        color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0') // Random color
      });
      
      setCategories(prev => [...prev, newCategory]);
      setNewWord(prev => ({ ...prev, category: newCategory.id }));
      setShowNewCategoryInput(false);
      setNewCategoryName('');
      
      toast({
        title: "Category Created",
        description: `&ldquo;${newCategory.name}&rdquo; has been added`,
      });
    } catch (error) {
      toast({
        title: "Error Creating Category",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setCreatingCategory(false);
    }
  };


  const handleDialogOpenChange = (open) => {
    if (open) {
      // Dialog is opening
      setIsAddDialogOpen(true);
    } else {
      // Dialog is closing - reset state
      setIsAddDialogOpen(false);
      setEditingWord(null);
      setShowNewCategoryInput(false);
      setNewCategoryName('');
      setNewWord({
        algerianWord: '',
        englishTranslation: '',
        pronunciation: '',
        arabicScript: '',
        exampleSentence: '',
        category: '',
        difficulty: 'beginner',
        wordType: '',
        possessiveType: ''
      });
    }
  };
  
  const handleAddWord = async () => {
    if (!newWord.algerianWord || !newWord.englishTranslation || !newWord.category) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingWord) {
        const updatedWord = await vocabularyAPI.update(editingWord.id, newWord);
        
        // Update the specific word in the list instead of full refresh
        setWords(prevWords => 
          prevWords.map(word => 
            word.id === editingWord.id 
              ? { ...word, ...updatedWord } 
              : word
          )
        );
        
        toast({
          title: "Word Updated Successfully",
          description: `"${newWord.algerianWord}" has been updated`,
        });
      } else {
        const createdWord = await vocabularyAPI.create(newWord);
        
        // Add the new word to the beginning of the list
        setWords(prevWords => [createdWord, ...prevWords]);
        
        toast({
          title: "Word Added Successfully",
          description: `"${newWord.algerianWord}" has been added to your vocabulary`,
        });
      }
      
      // Reset form
      setNewWord({
        algerianWord: '',
        englishTranslation: '',
        pronunciation: '',
        arabicScript: '',
        exampleSentence: '',
        category: '',
        difficulty: 'beginner',
        wordType: '',
        possessiveType: ''
      });
      setEditingWord(null);
      setIsAddDialogOpen(false);
      
    } catch (error) {
      toast({
        title: editingWord ? "Error Updating Word" : "Error Adding Word",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditWord = (word) => {
    saveScrollPosition();
    setEditingWord(word);
    setNewWord({
      algerianWord: word.algerianWord,
      englishTranslation: word.englishTranslation,
      pronunciation: word.pronunciation || '',
      arabicScript: word.arabicScript || '',
      exampleSentence: word.exampleSentence || '',
      category: word.category,
      difficulty: word.difficulty,
      wordType: word.wordType || '',
      possessiveType: word.possessiveType || ''
    });
    setIsAddDialogOpen(true);
  };

  const handleDeleteWord = async (wordId) => {
    if (!window.confirm('Are you sure you want to delete this word?')) {
      return;
    }

    try {
      saveScrollPosition();
      await vocabularyAPI.delete(wordId);
      
      // Remove the word from the list instead of full refresh
      setWords(prevWords => prevWords.filter(word => word.id !== wordId));
      
      toast({
        title: "Word Deleted",
        description: "Vocabulary word has been removed",
      });
      
      restoreScrollPosition();
    } catch (error) {
      toast({
        title: "Error Deleting Word",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const playPronunciation = async (word) => {
    // Check if we have recorded audio
    if (word.audioUrl) {
      try {
        // Handle both Cloudinary URLs (https://) and local URLs (/api/audio/)
        let audioUrl;
        if (word.audioUrl.startsWith('http')) {
          // Cloudinary URL - use directly
          audioUrl = word.audioUrl;
        } else {
          // Local URL - prepend backend URL with cache-busting
          const cacheBuster = `?t=${Date.now()}`;
          audioUrl = `${process.env.REACT_APP_BACKEND_URL}${word.audioUrl}${cacheBuster}`;
        }
        
        toast({
          title: "🔊 Playing Authentic Pronunciation",
          description: `${word.algerianWord} (recorded by native speaker)`,
        });

        // Play recorded audio with proper CORS settings
        const audio = new Audio();
        audio.crossOrigin = "anonymous"; // Enable CORS
        audio.src = audioUrl;
        
        // Handle play promise
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('✅ Audio playing successfully:', audioUrl);
            })
            .catch(error => {
              console.error('❌ Audio playback failed:', {
                error: error.message,
                name: error.name,
                url: audioUrl,
                word: word.algerianWord
              });
              toast({
                title: "Audio Playback Failed",
                description: `${error.name}: Falling back to synthetic pronunciation`,
                variant: "destructive"
              });
              // Fall back to text-to-speech
              playSyntheticSpeech(word);
            });
        }
        
        return;
      } catch (error) {
        console.error('Audio setup failed:', error);
        // Fall back to text-to-speech
      }
    }

    // Fallback to text-to-speech
    playSyntheticSpeech(word);
  };
  
  const playSyntheticSpeech = (word) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word.arabicScript || word.algerianWord);
      
      utterance.onstart = () => {
        toast({
          title: "Playing Pronunciation",
          description: `${word.algerianWord} (${word.pronunciation || 'synthetic voice'})`,
        });
      };
      
      window.speechSynthesis.speak(utterance);
    } else {
      toast({
        title: "Pronunciation Guide",
        description: `${word.algerianWord}: ${word.pronunciation || 'No guide available'}`,
      });
    }
  };

  const getCategoryColor = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.color : '#1B4E3F';
  };

  const handleAudioUploadSuccess = (audioUrl) => {
    // Update only the specific word instead of reloading entire list
    if (recordingWord) {
      setWords(prevWords => 
        prevWords.map(word => 
          word.id === recordingWord.id 
            ? { ...word, audioUrl } 
            : word
        )
      );
      setUpdatingWordId(null);
    }
    
    setRecordingWord(null);
    toast({
      title: "Audio recorded successfully!",
      description: "Your authentic pronunciation has been saved.",
    });
    
    // Restore scroll position
    restoreScrollPosition();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Vocabulary Manager</h1>
        <p className="text-gray-600">Build your Algerian dialect vocabulary with structured learning</p>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search vocabulary..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(category => (
              <SelectItem key={category.id} value={category.id}>
                {category.icon} {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {user?.is_admin && (
          <Dialog open={isAddDialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Word
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingWord ? 'Edit Word' : 'Add New Word'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="algerian">Algerian Word *</Label>
                <Input
                  id="algerian"
                  value={newWord.algerianWord}
                  onChange={(e) => setNewWord(prev => ({ ...prev, algerianWord: e.target.value }))}
                  placeholder="Enter Algerian word"
                />
              </div>
              
              <div>
                <Label htmlFor="english">English Translation *</Label>
                <Input
                  id="english"
                  value={newWord.englishTranslation}
                  onChange={(e) => setNewWord(prev => ({ ...prev, englishTranslation: e.target.value }))}
                  placeholder="Enter English translation"
                />
              </div>
              
              <div>
                <Label htmlFor="pronunciation">Pronunciation Guide</Label>
                <Input
                  id="pronunciation"
                  value={newWord.pronunciation}
                  onChange={(e) => setNewWord(prev => ({ ...prev, pronunciation: e.target.value }))}
                  placeholder="e.g., sa-LAAM"
                />
              </div>

              <div>
                <Label htmlFor="arabicScript">Arabic Script (Optional)</Label>
                <Input
                  id="arabicScript"
                  value={newWord.arabicScript}
                  onChange={(e) => setNewWord(prev => ({ ...prev, arabicScript: e.target.value }))}
                  placeholder="e.g., سلام"
                  dir="rtl"
                  className="text-right"
                />
                <p className="text-xs text-gray-500 mt-1">For better pronunciation accuracy</p>
              </div>
              
              <div>
                <Label htmlFor="example">Example Sentence</Label>
                <Textarea
                  id="example"
                  value={newWord.exampleSentence}
                  onChange={(e) => setNewWord(prev => ({ ...prev, exampleSentence: e.target.value }))}
                  placeholder="Enter example sentence"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  {!showNewCategoryInput ? (
                    <div className="space-y-2">
                      <Select value={newWord.category} onValueChange={(value) => {
                        if (value === '__create_new__') {
                          setShowNewCategoryInput(true);
                        } else {
                          setNewWord(prev => ({ ...prev, category: value }));
                        }
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(category => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.icon} {category.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="__create_new__" className="text-green-600 font-semibold">
                            + Create New Category
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          placeholder="New category name"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleCreateCategory();
                            }
                          }}
                          autoFocus
                        />
                        <Button 
                          type="button"
                          size="sm" 
                          onClick={handleCreateCategory}
                          disabled={creatingCategory || !newCategoryName.trim()}
                        >
                          {creatingCategory ? <Loader className="w-4 h-4 animate-spin" /> : 'Add'}
                        </Button>
                        <Button 
                          type="button"
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">Press Enter or click Add to create</p>
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="difficulty">Difficulty</Label>
                  <Select value={newWord.difficulty} onValueChange={(value) => setNewWord(prev => ({ ...prev, difficulty: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Word Type and Possessive Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="wordType">Word Type</Label>
                  <Select value={newWord.wordType || ''} onValueChange={(value) => setNewWord(prev => ({ ...prev, wordType: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="noun">Noun</SelectItem>
                      <SelectItem value="verb">Verb</SelectItem>
                      <SelectItem value="adjective">Adjective</SelectItem>
                      <SelectItem value="phrase">Phrase</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="possessiveType">Possessive Type</Label>
                  <Select value={newWord.possessiveType || ''} onValueChange={(value) => setNewWord(prev => ({ ...prev, possessiveType: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct (-i, -ek, etc.)</SelectItem>
                      <SelectItem value="indirect">Indirect (diyal/ta3)</SelectItem>
                      <SelectItem value="both">Both patterns</SelectItem>
                      <SelectItem value="none">Not applicable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => handleDialogOpenChange(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button onClick={handleAddWord} disabled={submitting}>
                  {submitting && <Loader className="w-4 h-4 mr-2 animate-spin" />}
                  {editingWord ? 'Update' : 'Add'} Word
                </Button>
              </div>
            </div>
          </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-green-600" />
          <p className="text-gray-600">Loading vocabulary...</p>
        </div>
      )}

      {/* Vocabulary Grid */}
      {!loading && words.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {words.map(word => (
            <Card key={word.id} className={`hover:shadow-lg transition-all duration-200 border-l-4 ${updatingWordId === word.id ? 'opacity-75 pointer-events-none' : ''}`} style={{ borderLeftColor: getCategoryColor(word.category) }}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                      {word.algerianWord}
                      {updatingWordId === word.id && (
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      )}
                    </CardTitle>
                    <p className="text-gray-600 font-medium">{word.englishTranslation}</p>
                  </div>
                  <div className="flex space-x-1">
                    {word.pronunciation && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => playPronunciation(word)}
                        className="p-2 hover:bg-green-50"
                        title={word.audioUrl ? 'Play recorded audio' : 'Play synthetic pronunciation'}
                      >
                        <Volume2 className={`w-4 h-4 ${word.audioUrl ? 'text-blue-600' : 'text-green-600'}`} />
                      </Button>
                    )}
                    {user?.is_admin && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            saveScrollPosition();
                            setUpdatingWordId(word.id);
                            setRecordingWord(word);
                          }}
                          className="p-2 hover:bg-orange-50"
                          title={word.audioUrl ? "Re-record pronunciation" : "Record pronunciation"}
                          disabled={updatingWordId === word.id}
                        >
                          <Mic className={`w-4 h-4 ${word.audioUrl ? 'text-orange-600' : 'text-gray-600'}`} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            saveScrollPosition();
                            handleEditWord(word);
                          }}
                          className="p-2 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteWord(word.id)}
                          className="p-2 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {word.arabicScript && (
                  <p className="text-lg text-blue-800 mb-2 font-medium" dir="rtl">
                    {word.arabicScript}
                  </p>
                )}
                
                {word.pronunciation && (
                  <p className="text-sm text-gray-500 mb-2 italic">
                    Pronunciation: {word.pronunciation}
                  </p>
                )}
                
                {word.exampleSentence && (
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    &ldquo;{word.exampleSentence}&rdquo;
                  </p>
                )}
                
                <div className="flex justify-between items-center">
                  <Badge 
                    variant="secondary" 
                    className="text-xs"
                    style={{ backgroundColor: `${getCategoryColor(word.category)}20`, color: getCategoryColor(word.category) }}
                  >
                    {categories.find(cat => cat.id === word.category)?.name}
                  </Badge>
                  <Badge 
                    variant={word.difficulty === 'beginner' ? 'default' : word.difficulty === 'intermediate' ? 'secondary' : 'destructive'}
                    className="text-xs"
                  >
                    {word.difficulty}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && words.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-500 mb-2">No vocabulary found</h3>
          <p className="text-gray-400 mb-4">
            {searchTerm || selectedCategory !== 'all' ? 'Try adjusting your filters' : user?.is_admin ? 'Start building your vocabulary' : 'No words available yet'}
          </p>
          {user?.is_admin && (
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Word
            </Button>
          )}
        </div>
      )}

      {/* Audio Recorder Dialog */}
      {recordingWord && (
        <Dialog open={!!recordingWord} onOpenChange={(open) => {
          if (!open) {
            setRecordingWord(null);
            setUpdatingWordId(null);
            restoreScrollPosition();
          }
        }}>
          <DialogContent className="max-w-md">
            <AudioRecorder
              wordId={recordingWord.id}
              wordText={recordingWord.algerianWord}
              onUploadSuccess={handleAudioUploadSuccess}
              onClose={() => {
                setRecordingWord(null);
                setUpdatingWordId(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default VocabularyManager;